import { useState, useEffect, useRef, useCallback } from 'react';
import { doc, getDoc, collection, getDocs } from 'firebase/firestore';
import { db } from '../../../database/config';
import { useHomeData } from '../../../database/HomeDataContext';
import { Body3Skeleton } from '../../Skeletons/Skeletons';

const Body3 = () => {
  const { data: homeData, isOutsideProvider } = useHomeData();
  const [localData, setLocalData] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const intervalRef = useRef(null);

  // Fallback fetch for when used outside HomeDataProvider
  useEffect(() => {
    if (!isOutsideProvider) return;
    const fetchBody3Data = async () => {
      try {
        const [headingSnap, subHeadingSnap, stepCardsSnap] = await Promise.all([
          getDoc(doc(db, 'home', 'body3', 'heading', 'heading')),
          getDoc(doc(db, 'home', 'body3', 'subHeading', 'subHeading')),
          getDocs(collection(db, 'home', 'body3', 'stepCards')),
        ]);
        const steps = [];
        const sortedDocs = stepCardsSnap.docs.sort((a, b) => {
          const aNum = parseInt(a.id.replace('stepCard', ''));
          const bNum = parseInt(b.id.replace('stepCard', ''));
          return aNum - bNum;
        });
        sortedDocs.forEach((d, index) => {
          const data = d.data();
          steps.push({
            number: index + 1,
            title: data.heading || `Step ${index + 1}`,
            description: data.content || '',
            name: data.name || data.heading || `Step ${index + 1}`,
            icon: ['🍳', '🥗', '⏰', '🛵'][index] || '📱',
            phoneMockupImage: data.phoneMockupImage || '',
          });
        });
        setLocalData({
          heading: headingSnap.exists() ? (headingSnap.data().text || '') : '',
          subHeading: subHeadingSnap.exists() ? (subHeadingSnap.data().text || '') : '',
          steps,
        });
      } catch (error) {
        console.error('Error fetching Body3 data:', error);
      }
    };
    fetchBody3Data();
  }, [isOutsideProvider]);

  const body3Data = isOutsideProvider ? localData : homeData?.body3;
  const steps = body3Data?.steps || [];

  const startTimer = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % steps.length);
    }, 6000);
  }, [steps.length]);

  const handleStepClick = useCallback((index) => {
    setActiveStep(index);
    startTimer();
  }, [startTimer]);

  useEffect(() => {
    if (steps.length === 0) return;
    startTimer();
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [steps.length, startTimer]);

  if (!body3Data || steps.length === 0) {
    return <Body3Skeleton />;
  }

  const currentStep = steps[activeStep];

  return (
    <section id="how-it-works" className="scroll-mt-16 md:scroll-mt-10 lg:scroll-mt-10 py-12 sm:py-20 bg-orange-50/50 overflow-hidden relative">
      {/* Background ambient glows */}
      <div className="absolute top-1/4 left-0 w-64 h-64 bg-orange-200/40 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-0 right-10 w-80 h-80 bg-amber-200/30 rounded-full blur-3xl pointer-events-none"></div>

      {/* Heading */}
      <div className="max-w-4xl mx-auto text-center mb-10 sm:mb-20 px-6">
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-gray-900 mb-4 tracking-tight">
          {body3Data.heading}
        </h2>
        <p className="text-gray-600 text-base sm:text-xl">
          {body3Data.subHeading}
        </p>
      </div>

      {/* Main Container: Strict Side-by-Side to prevent overlap */}
      <div className="max-w-6xl mx-auto flex flex-row items-center px-4 sm:px-10 lg:px-20 relative z-10">

        {/* Left Side: Floating Phone Image (Increased min-h for massive scaling) */}
        <div className="w-[75%] sm:w-1/2 relative min-h-[420px] sm:min-h-[600px] lg:min-h-[650px]">
          {currentStep?.phoneMockupImage ? (
            <img
              key={`img-${activeStep}`}
              src={currentStep.phoneMockupImage}
              alt={`${currentStep.name} Screen`}
              // Added scale-[1.2] for mobile, scale-125 for tablet, scale-[1.35] for desktop
              className="absolute inset-0 w-full h-full object-contain object-center drop-shadow-2xl animate-fade-in scale-[1.2] sm:scale-125 lg:scale-[1.35] transform-gpu origin-center"
              style={{ animation: 'fadeIn 0.4s ease-out forwards' }}
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-[80%] h-[80%] bg-white/50 border-4 border-white rounded-[2rem] shadow-xl flex items-center justify-center">
                <span className="text-gray-400 text-sm">No Image</span>
              </div>
            </div>
          )}
        </div>

        {/* Right Side: Floating Text & Controls */}
        <div className="w-[55%] sm:w-1/2 flex flex-col justify-center pl-2 sm:pl-10">

          {/* Text Container with Minimum Height to prevent button stutter/jumping */}
          <div className="min-h-[220px] sm:min-h-[240px] lg:min-h-[200px] flex flex-col justify-center">
            <div
              key={`text-${activeStep}`}
              className="animate-fade-in-up"
              style={{ animation: 'fadeInUp 0.4s ease-out forwards' }}
            >
              {/* Step Badge */}
              <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-4">
                <span className="text-xl sm:text-2xl">{currentStep.icon}</span>
                <span className="text-orange-600 font-bold tracking-widest text-[10px] sm:text-sm uppercase">
                  Step {currentStep.number}
                </span>
              </div>

              {/* Title & Description */}
              <h3 className="text-xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2 sm:mb-4 leading-tight">
                {currentStep.title}
              </h3>
              <p className="text-gray-600 text-xs sm:text-base lg:text-lg leading-relaxed max-w-md">
                {currentStep.description}
              </p>
            </div>
          </div>

          {/* Navigation Controls - Placed OUTSIDE the animated div so they NEVER stutter */}
          <div className="flex items-center gap-4 sm:gap-8 mt-4 sm:mt-6">
            <button
              onClick={() => handleStepClick(Math.max(0, activeStep - 1))}
              disabled={activeStep === 0}
              className={`p-2 -ml-2 transition-transform hover:scale-110 ${activeStep === 0 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-900'}`}
            >
              <svg className="w-5 h-5 sm:w-7 sm:h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            {/* Minimalist Dots */}
            <div className="flex gap-2 sm:gap-3">
              {steps.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => handleStepClick(idx)}
                  className={`h-2 rounded-full transition-all duration-300 ${idx === activeStep
                      ? 'bg-orange-500 w-6 sm:w-8'
                      : 'bg-orange-200 hover:bg-orange-400 w-2'
                    }`}
                  aria-label={`Go to step ${idx + 1}`}
                />
              ))}
            </div>

            <button
              onClick={() => handleStepClick(Math.min(steps.length - 1, activeStep + 1))}
              disabled={activeStep === steps.length - 1}
              className={`p-2 transition-transform hover:scale-110 ${activeStep === steps.length - 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-900'}`}
            >
              <svg className="w-5 h-5 sm:w-7 sm:h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>

      </div>

      <style dangerouslySetInnerHTML={{
        __html: `
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.98); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}} />
    </section>
  );
};

export default Body3;